/*
 * Cookieless CRO measurement for Hjartgruppen, Milistuff and Medstuff.
 *
 * Vercel Web Analytics owns page views and the custom events below. The shop
 * stays on Shopify; hjartgruppen-automations.vercel.app is only the intake
 * project. A text/plain beacon goes through the project's narrow analytics
 * relay, which validates the public page URL and event allowlist before the
 * official Vercel server SDK preserves the visitor's request context.
 *
 * Privacy invariants:
 *   - never load on account pages, previews, or unlisted storefronts
 *   - never send search phrases, form values, e-mail addresses, order ids or
 *     free text
 *   - retain only campaign query parameters on automatic page views
 *   - analytics failure must never affect a shopping action
 */
(function () {
  'use strict';

  var hostname = window.location.hostname.toLowerCase();
  var allowedHosts = {
    'hjartgruppenshop.se': true,
    'www.hjartgruppenshop.se': true,
    'milistuff.se': true,
    'www.milistuff.se': true,
    'milistuff.dk': true,
    'www.milistuff.dk': true,
    'milistuff.de': true,
    'www.milistuff.de': true,
    'milistuff.fi': true,
    'www.milistuff.fi': true,
    'milistuff.com': true,
    'www.milistuff.com': true,
    'medstuffse.myshopify.com': true
  };
  if (!allowedHosts[hostname]) return;
  if (/^\/account(?:\/|$)/.test(window.location.pathname)) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (
    window.Shopify &&
    window.Shopify.theme &&
    window.Shopify.theme.role &&
    window.Shopify.theme.role !== 'main'
  ) return;

  var ANALYTICS_ENDPOINT = 'https://hjartgruppen-automations.vercel.app/api/shop-analytics';
  var MAX_TEXT_LENGTH = 120;
  var pendingAdd = null;
  var engaged = false;

  function analyticsUrl() {
    try {
      var url = new URL(window.location.href);
      var allowed = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'];
      var queryKeys = [];
      url.searchParams.forEach(function (_value, key) {
        queryKeys.push(key);
      });
      queryKeys.forEach(function (key) {
        if (allowed.indexOf(key) === -1) url.searchParams.delete(key);
      });
      url.hash = '';
      return url.toString();
    } catch (_error) {
      return window.location.origin + window.location.pathname;
    }
  }

  function sendVercel(name, data) {
    if (navigator.webdriver || /Headless/i.test(navigator.userAgent || '')) return;
    var body = JSON.stringify({ name: name, data: data, url: analyticsUrl() });

    try {
      if (navigator.sendBeacon && navigator.sendBeacon(ANALYTICS_ENDPOINT, body)) return;
    } catch (_error) {
      /* Fall through to a non-blocking no-CORS request. */
    }

    try {
      window.fetch(ANALYTICS_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: body
      });
    } catch (_error) {
      /* Measurement is never allowed to affect the customer. */
    }
  }

  function recordPageview() {
    sendVercel('page_view', cleanProperties(pageContext()));
  }

  function pageType() {
    var match = document.body.className.match(/(?:^|\s)template-([^\s]+)/);
    return match ? match[1] : 'other';
  }

  function handleFromPath(path) {
    var patterns = [
      /\/products\/([^/?#]+)/,
      /\/collections\/([^/?#]+)/,
      /\/blogs\/[^/?#]+\/([^/?#]+)/
    ];
    for (var i = 0; i < patterns.length; i += 1) {
      var match = String(path || '').match(patterns[i]);
      if (match) return decodeURIComponent(match[1]).slice(0, MAX_TEXT_LENGTH);
    }
    return pageType() === 'index' ? 'home' : '';
  }

  function pageContext() {
    return {
      page_type: pageType(),
      page_handle: handleFromPath(window.location.pathname)
    };
  }

  function cleanProperties(properties) {
    var cleaned = {};
    Object.keys(properties || {}).forEach(function (key) {
      var value = properties[key];
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'string') {
        cleaned[key] = value.slice(0, MAX_TEXT_LENGTH);
      } else if (typeof value === 'number' && isFinite(value)) {
        cleaned[key] = value;
      } else if (typeof value === 'boolean') {
        cleaned[key] = value;
      }
    });
    return cleaned;
  }

  /*
   * Base Pro accepts at most two custom data properties per Vercel event.
   * Keep the richer payload for Shopify subscribers, but send only the two
   * dimensions that are most useful for each CRO question to Vercel. The page
   * URL is already attached by Web Analytics, so it need not be repeated.
   */
  function vercelProperties(name, payload) {
    var selected = {};

    function take(key, alias) {
      if (payload[key] === undefined || payload[key] === null || payload[key] === '') return;
      selected[alias || key] = payload[key];
    }

    if (name === 'product_list_viewed' || name === 'product_list_extended' || name === 'product_list_load_more') {
      take('list_name');
      take('item_count');
    } else if (name === 'product_selected') {
      take('product_handle');
      if (payload.list_name) {
        selected.list_slot = payload.list_name + (payload.position ? ':' + payload.position : '');
      }
    } else if (name === 'collection_selected') {
      take('collection_handle');
      take('placement');
    } else if (name === 'guide_selected') {
      take('guide_handle');
      take('placement');
    } else if (name === 'comparison_product_selected') {
      take('product_handle');
      take('placement');
    } else if (name === 'product_gallery_used') {
      take('product_handle');
      take('image_position');
    } else if (name === 'quote_email_opened') {
      take('product_handle');
      take('price_band');
    } else if (name === 'collection_text_toggled') {
      take('action');
      take('page_handle');
    } else if (name === 'sort_changed') {
      take('sort');
      take('page_handle');
    } else if (name === 'filter_changed') {
      take('filter');
      take('action');
    } else if (name === 'add_to_cart') {
      take('product_handle');
      take('placement');
    } else if (name === 'saved_product_changed') {
      take('product_handle');
      take('action');
    } else if (name === 'page_engaged') {
      take('page_type');
      take('signal');
    }

    return cleanProperties(selected);
  }

  function publishShopify(name, properties) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('hjartgruppen:' + name, properties);
      }
    } catch (_error) {
      /* Measurement is never allowed to block the customer. */
    }
  }

  function track(name, properties, publish) {
    var payload = cleanProperties(Object.assign({}, pageContext(), properties || {}));
    sendVercel(name, vercelProperties(name, payload));
    if (publish !== false) publishShopify(name, payload);
  }

  function slug(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[åä]/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60);
  }

  function priceBand(rawCents) {
    var cents = parseInt(rawCents, 10);
    if (isNaN(cents)) return '';
    if (cents < 20000) return 'under_200';
    if (cents < 100000) return '200_999';
    if (cents < 300000) return '1000_2999';
    if (cents < 1000000) return '3000_9999';
    return '10000_plus';
  }

  function productCardData(card) {
    if (!card) return {};
    var grid = card.closest('.product-grid');
    var cards = grid ? Array.prototype.slice.call(grid.querySelectorAll('[data-analytics-product-card]')) : [];
    return {
      product_id: card.dataset.productId,
      product_handle: card.dataset.productHandle,
      variant_id: card.dataset.variantId,
      product_type: card.dataset.productType,
      vendor: card.dataset.productVendor,
      price_band: priceBand(card.dataset.productPrice),
      list_name: listName(grid),
      position: cards.indexOf(card) + 1
    };
  }

  function productPageData() {
    var product = document.querySelector('[data-analytics-product]');
    if (!product) return {};
    return {
      product_id: product.dataset.productId,
      product_handle: product.dataset.productHandle,
      variant_id: product.dataset.variantId,
      product_type: product.dataset.productType,
      vendor: product.dataset.productVendor,
      price_band: priceBand(product.dataset.productPrice)
    };
  }

  function listName(grid) {
    if (!grid) return '';
    if (grid.closest('.collection__content')) return 'collection_grid';
    if (grid.closest('.search__body')) return 'search_results';
    if (grid.closest('.cart-added')) return 'cart_recommendations';
    if (grid.closest('.home-bestsellers')) {
      var homeHeading = grid.closest('.home-bestsellers').querySelector('.section-head__heading');
      return 'home_' + (slug(homeHeading && homeHeading.textContent) || 'products');
    }
    if (pageType() === 'article') return 'guide_products';
    if (pageType() === 'product') {
      var block = grid.closest('.pdp-block, section');
      var productHeading = block && block.querySelector('h2');
      return 'product_' + (slug(productHeading && productHeading.textContent) || 'related');
    }
    return pageType() + '_products';
  }

  function placement(element) {
    if (!element) return 'unknown';
    if (element.closest('.bento-tile')) return 'home_bento';
    if (element.closest('.subcat-rail')) return pageType() + '_category_rail';
    if (element.closest('.cat-index__groups')) return pageType() + '_category_index';
    if (element.closest('.home-brands')) return 'home_brands';
    if (element.closest('.home-guides')) return 'home_guides';
    if (element.closest('.article-related')) return 'guide_related';
    if (element.closest('.pdp-compare')) return 'product_compare';
    if (element.closest('.breadcrumbs')) return 'breadcrumbs';
    var section = element.closest('section');
    var heading = section && section.querySelector('h1, h2');
    return pageType() + '_' + (slug(heading && heading.textContent) || 'content');
  }

  function itemHandle(href, kind) {
    try {
      var path = new URL(href, window.location.origin).pathname;
      var pattern = kind === 'product' ? /\/products\/([^/?#]+)/ : /\/collections\/([^/?#]+)/;
      var match = path.match(pattern);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_error) {
      return '';
    }
  }

  function observeProductLists() {
    var grids = Array.prototype.slice.call(document.querySelectorAll('.product-grid'));
    if (!grids.length) return;

    function record(grid) {
      if (grid.dataset.analyticsViewed === 'true') return;
      grid.dataset.analyticsViewed = 'true';
      track('product_list_viewed', {
        list_name: listName(grid),
        item_count: grid.querySelectorAll('[data-analytics-product-card]').length
      });
    }

    if (!('IntersectionObserver' in window)) {
      grids.forEach(record);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        record(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.05 });

    grids.forEach(function (grid) {
      observer.observe(grid);
    });

    document.addEventListener('product-cards:added', function (event) {
      var grid = event.detail && event.detail.root;
      if (!grid || !grid.matches('.product-grid')) return;
      track('product_list_extended', {
        list_name: listName(grid),
        item_count: grid.querySelectorAll('[data-analytics-product-card]').length
      });
    });
  }

  function recordEngagement(signal) {
    if (engaged) return;
    engaged = true;
    track('page_engaged', { signal: signal }, false);
  }

  function observeEngagement() {
    var visibleSeconds = 0;
    var timer = window.setInterval(function () {
      if (document.visibilityState !== 'hidden') visibleSeconds += 1;
      if (visibleSeconds >= 20) {
        window.clearInterval(timer);
        recordEngagement('20_seconds');
      }
    }, 1000);

    function onScroll() {
      var available = document.documentElement.scrollHeight - window.innerHeight;
      if (available <= 0 || window.scrollY / available < 0.5) return;
      window.removeEventListener('scroll', onScroll);
      recordEngagement('50_percent_scroll');
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  document.addEventListener('click', function (event) {
    var target = event.target.closest ? event.target.closest('a, button, label') : null;
    if (!target) return;

    var card = target.closest('[data-analytics-product-card]');
    var productLink = target.closest('a[href*="/products/"]');
    if (card && productLink) {
      track('product_selected', productCardData(card));
      return;
    }

    if (productLink && target.closest('.pdp-compare')) {
      track('comparison_product_selected', {
        placement: 'product_compare',
        product_handle: itemHandle(productLink.href, 'product')
      });
      return;
    }

    var collectionLink = target.closest('a[href*="/collections/"]');
    if (collectionLink && collectionLink.closest('main')) {
      track('collection_selected', {
        placement: placement(collectionLink),
        collection_handle: itemHandle(collectionLink.href, 'collection')
      });
      return;
    }

    var guideLink = target.closest('a[href*="/blogs/"]');
    if (guideLink && guideLink.closest('main')) {
      track('guide_selected', {
        placement: placement(guideLink),
        guide_handle: handleFromPath(guideLink.href)
      });
      return;
    }

    if (target.matches('.pdp-gallery__thumb')) {
      var galleryThumbs = Array.prototype.slice.call(target.parentNode.querySelectorAll('.pdp-gallery__thumb'));
      track('product_gallery_used', Object.assign({}, productPageData(), {
        image_position: galleryThumbs.indexOf(target) + 1
      }));
      return;
    }

    if (target.closest('.pdp-buy__quote')) {
      track('quote_email_opened', Object.assign({}, productPageData(), { placement: 'product_page' }), false);
      return;
    }

    if (target.closest('[data-clamp-toggle]')) {
      track('collection_text_toggled', {
        action: target.getAttribute('aria-expanded') === 'true' ? 'collapse' : 'expand'
      });
      return;
    }

    if (target.closest('[data-load-more]')) {
      var grid = document.querySelector('.product-grid');
      track('product_list_load_more', {
        list_name: listName(grid),
        item_count: grid ? grid.querySelectorAll('[data-analytics-product-card]').length : 0
      });
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    var form = input.closest && input.closest('.filters__form');
    if (!form) return;

    if (input.type === 'radio' && input.name === 'sort_by') {
      track('sort_changed', { sort: input.value });
      return;
    }

    if (input.type === 'checkbox') {
      track('filter_changed', {
        filter: input.name,
        action: input.checked ? 'add' : 'remove'
      });
      return;
    }

    if (input.closest('.filters__price')) {
      track('filter_changed', {
        filter: 'price',
        action: input.value.trim() ? 'set' : 'clear'
      });
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches || !form.matches('[data-cart-add-form]')) return;

    var card = form.closest('[data-analytics-product-card]');
    var quantityInput = form.querySelector('[name="quantity"]');
    pendingAdd = Object.assign({}, card ? productCardData(card) : productPageData(), {
      placement: card ? listName(card.closest('.product-grid')) : 'product_page',
      quantity: quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1,
      started_at: Date.now()
    });
  }, true);

  document.addEventListener('cart:item-added', function () {
    if (!pendingAdd) return;
    var payload = pendingAdd;
    pendingAdd = null;
    if (Date.now() - payload.started_at > 15000) return;
    delete payload.started_at;
    track('add_to_cart', payload);
  });

  document.addEventListener('saved-products:updated', function (event) {
    var detail = event.detail || {};
    if (!detail.action || detail.action === 'storage') return;
    var item = detail.item || {};
    track('saved_product_changed', {
      action: detail.action,
      source: detail.source,
      product_handle: item.handle,
      variant_id: item.variantId,
      total_saved: Array.isArray(detail.items) ? detail.items.length : 0
    }, false);
  });

  recordPageview();
  observeProductLists();
  observeEngagement();
})();
